import type { CSSProperties } from "react";

import {
  ARTICLE_INNER_CLASS,
  ARTICLE_WRAPPER_CLASS,
  P_CLASS,
  PULL_QUOTE_CLASS,
} from "../prose";
import { UseCaseHero } from "../UseCaseHero";
import { UseCaseLayout } from "../UseCaseLayout";
import { UseCaseVideo } from "../UseCaseVideo";

const TITLE =
  "How Companion Supports an Unfamiliar Case from Intake to Referral 🦔✨";
const VIDEO_TITLE =
  "How Companion Supports an Unfamiliar Case from Intake to Referral";
const DECK =
  "Differentials, a communication plan, a client handout, and a specialist referral email — all from one conversation with Companion.";

type CompanionAccent = "quill" | "summi" | "sage" | "otto";

const accentColor: Record<CompanionAccent, string> = {
  quill: "#D97706",
  summi: "#DB2777",
  sage: "#059669",
  otto: "#2563EB",
};

function CompanionTag({
  emoji,
  name,
  accent,
}: {
  emoji: string;
  name: string;
  accent: CompanionAccent;
}) {
  return (
    <span
      className="font-bold"
      style={{ color: accentColor[accent] } as CSSProperties}
    >
      {name} {emoji}
    </span>
  );
}

export function UnfamiliarCasePage() {
  return (
    <UseCaseLayout>
      <UseCaseHero title={TITLE} deck={DECK} />
      <UseCaseVideo
        embedUrl="https://www.youtube.com/embed/wWtuyJ-AH4E"
        title={VIDEO_TITLE}
      />
      <article className={ARTICLE_WRAPPER_CLASS}>
        <div className={ARTICLE_INNER_CLASS}>
          <p className={P_CLASS}>
            An ER vet told me last week she keeps ChatGPT open in another tab
            while using Scribenote.
          </p>

          <p className={P_CLASS}>
            Not because Scribenote doesn&apos;t work. Because at 2am, she needs
            discharge instructions fast — and our workflow had too many steps.
            She&apos;d draft the SOAP, copy-paste the case into ChatGPT for the
            client email, then copy it back.
          </p>

          <p className={P_CLASS}>
            She&apos;s been with us over a year. Works overnight ER. Engineers
            her own prompts. Has strong formatting opinions. She tried other
            tools and came back. She loves Scribenote — she just needed it to do
            more.
          </p>

          <p className={P_CLASS}>So I showed her Companion 2.0.</p>

          <p className={P_CLASS}>
            The demo: a critical patient case. She needed a discharge summary
            and a transfer note. Normally she&apos;d finish the SOAP, switch to
            ChatGPT, reconstruct the case, draft the discharge, copy it back,
            format it, then write the transfer note separately.
          </p>

          <p className={P_CLASS}>
            With Companion, she asked{" "}
            <CompanionTag emoji="🐕" name="Otto" accent="otto" /> to draft both.
            Thirty seconds for the discharge. Thirty for the transfer. Both
            pulled from her SOAP. Both formatted right. No tab-switching. No
            copy-pasting.
          </p>

          <blockquote className={PULL_QUOTE_CLASS}>
            “This is what I&apos;ve been asking for.”
          </blockquote>

          <p className={P_CLASS}>
            That&apos;s why we built Companion 2.0. Four AI assistants that work
            the way vets think.{" "}
            <CompanionTag emoji="🐘" name="Summi" accent="summi" /> reviews
            patient histories.{" "}
            <CompanionTag emoji="🦔" name="Quill" accent="quill" /> builds
            templates in plain language.{" "}
            <CompanionTag emoji="🐢" name="Sage" accent="sage" /> helps with
            differentials. <CompanionTag emoji="🐕" name="Otto" accent="otto" />{" "}
            handles emails and referrals.
          </p>

          <p className={P_CLASS}>
            Companion 2.0 is live. If you&apos;re tired of keeping ChatGPT open
            because your tools don&apos;t talk to each other, this is for you.
          </p>

          <p className={P_CLASS}>
            <a
              href="https://app.scribenote.com/dashboard?companion"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-indigo-600 underline underline-offset-2"
            >
              Try Companion
            </a>
            . Ask it to draft your next discharge summary, and see what happens.
          </p>
        </div>
      </article>
    </UseCaseLayout>
  );
}
